// Stable per-device identifier for anonymous, device-scoped features such as
// Openings photo likes. Persisted in localStorage so the same browser reuses
// its id across visits. Likes are attributed to this id; they intentionally do
// not follow a user across devices/browsers.

const DEVICE_ID_KEY = "openings_device_id";

const createId = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers without crypto.randomUUID.
  return "dev-xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Returns the stable device id, creating and persisting one on first use.
 * Returns null during SSR (no localStorage). Callers should guard for null.
 */
export const getDeviceId = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    let id = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!id || id.length < 1 || id.length > 128) {
      id = createId();
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable (private mode, blocked cookies) — fall back to a
    // volatile id so likes still work for the current session.
    return createId();
  }
};
